import { Component, OnInit, OnChanges, DoCheck, Input, ViewChild, ChangeDetectorRef, SimpleChanges, KeyValueDiffers, KeyValueDiffer, IterableDiffers, IterableDiffer } from '@angular/core';
import { Observable } from 'rxjs';
import { BaseChartDirective } from 'ng2-charts';
import {
  FilterState,
  ChartDataset,
  ChartColorConfig,
  ChartUpdateService,
  AnalyticsHttpService,
  AnalyticsExportService,
  getBarChartOptions,
  getDoughnutChartOptions,
  createConcentricDonutDatasets,
  createSingleDonutDataset,
  ANALYTICS_COLORS,
  CHART_UPDATE_DELAY,
  sortBarChartData,
  limitBarChartData,
  SortDirection,
  DEFAULT_CHART_ITEM_LIMIT,
  calculateFirstDatasetTotal,
  calculateArrayTotal,
  truncateLabel as truncateLabelUtil,
  getBreakdownDisplayName
} from 'app/core/analytics';

@Component({
  selector: 'kt-drink-volume-tab',
  templateUrl: './drink-volume-tab.component.html',
  styleUrls: [
    '../../analytics.component.scss',
    './drink-volume-tab.component.scss'
  ]
})
export class DrinkVolumeTabComponent implements OnInit, OnChanges, DoCheck {
  private readonly revenueChartGreen = '#8fbf5a';

  @Input() filters!: FilterState;
  @Input() drinkOptions?: Array<{ value: string | number; label: string }>;
  @Input() productOptions?: Array<{ value: string | number; label: string }>;
  @Input() distributorOptions?: Array<{ value: string | number; label: string }>;
  @Input() marketOptions?: Array<{ value: string | number; label: string }>;
  @Input() clientOptions?: Array<{ value: string | number; label: string }>;
  @Input() campusOptions?: Array<{ value: string | number; label: string }>;
  @Input() machineOptions?: Array<{ value: string | number; label: string }>;
  @ViewChild('drinkVolumeBarChart', { static: false }) drinkVolumeBarChart!: BaseChartDirective;
  @ViewChild('drinkVolumeBreakdownDonutChart', { static: false }) drinkVolumeBreakdownDonutChart!: BaseChartDirective;
  @ViewChild('machineDrinkBarChart', { static: false }) machineDrinkBarChart!: BaseChartDirective;
  @ViewChild('machineDonutChart', { static: false }) machineDonutChart!: BaseChartDirective;

  public drinkVolumeBarChartOptions = getBarChartOptions();
  public machineDrinkBarChartOptions = getBarChartOptions();
  public breakdownDonutChartOptions = getDoughnutChartOptions(false);
  public machineDonutChartOptions = getDoughnutChartOptions(false);

  public drinkVolumeBarColors: ChartColorConfig[] = [];
  public machineDrinkBarColors: ChartColorConfig[] = [];
  public donutChartColors: ChartColorConfig[] = [];

  public drinkVolumeBarChartLabels: string[] = [];
  public drinkVolumeBarChartData: ChartDataset[] = [];
  public drinkVolumeCompareDataArray: number[] = [];
  public drinkVolumeChartDisplayLabels: string[] = [];
  public drinkVolumeChartDisplayData: ChartDataset[] = [];
  public drinkVolumeChartDisplayCompareData: number[] = [];
  public drinkVolumeBarChartSort: SortDirection = 'desc';
  public showAllDrinkVolumeItems = false;

  private originalDrinkVolumeBarChartLabels: string[] = [];
  private originalDrinkVolumeBarChartData: ChartDataset[] = [];
  private originalDrinkVolumeCompareDataArray: number[] = [];

  private fullDrinkVolumeBarChartLabels: string[] = [];
  private fullDrinkVolumeBarChartData: ChartDataset[] = [];
  private fullDrinkVolumeCompareDataArray: number[] = [];

  public drinkVolumeBreakdownDonutLabels: string[] = [];
  public drinkVolumeBreakdownDonutData: ChartDataset[] = [];
  public breakdownDonutChartKey: number = 1;

  public machineDrinkBarLabels: string[] = [];
  public machineDrinkBarData: ChartDataset[] = [];
  public machineDrinkCompareDataArray: number[] = [];
  public machineDrinkBarChartSort: SortDirection = 'desc';
  public showAllMachineDrinkItems = false;

  private originalMachineDrinkBarLabels: string[] = [];
  private originalMachineDrinkBarData: ChartDataset[] = [];
  private originalMachineDrinkCompareDataArray: number[] = [];

  private fullMachineDrinkBarLabels: string[] = [];
  private fullMachineDrinkBarData: ChartDataset[] = [];
  private fullMachineDrinkCompareDataArray: number[] = [];

  private drinkVolumeApiTotal: number = 0;
  private machineDrinkApiTotal: number = 0;
  public drinkVolumeRetailRevenue = 0;
  public drinkVolumeCompareRetailRevenue = 0;
  private drinkVolumeRetailRevenueByLabel: Map<string, number> = new Map();
  private drinkVolumeCompareRetailRevenueByLabel: Map<string, number> = new Map();

  public machineDonutLabels: string[] = [];
  public machineDonutData: ChartDataset[] = [];
  public machineDonutChartKey: number = 1;

  public isLoadingDrinkVolume = false;
  public isLoadingDrinkVolumeBreakdown = false;
  public isLoadingMachineDrinkVolume = false;
  public isLoadingMachineBreakdown = false;

  private isDestroyingCharts = false;
  private filtersDiffer: KeyValueDiffer<string, any> | null = null;
  private arrayDiffers: Map<string, IterableDiffer<string>> = new Map();
  private lastObservedSaleScope: FilterState['saleScope'] | null = null;

  // Mock contextual data mapping for tooltips
  private contextualDataMap: Map<string, { partner?: string; client?: string; campus?: string }> = new Map();

  // Custom tooltip state
  public tooltipVisible = false;
  public tooltipX = 0;
  public tooltipY = 0;
  public tooltipText = '';
  public chartMetricMode: 'volume' | 'revenue' = 'volume';

  constructor(
    private analyticsHttpService: AnalyticsHttpService,
    private chartUpdateService: ChartUpdateService,
    private analyticsExportService: AnalyticsExportService,
    private cdr: ChangeDetectorRef,
    private keyValueDiffers: KeyValueDiffers,
    private iterableDiffers: IterableDiffers
  ) {
    this.initializeColors();
  }

  ngOnInit(): void {
    this.filtersDiffer = this.keyValueDiffers.find(this.filters).create();
    this.initializeArrayDiffers();
    this.loadAllData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['filters'] && !changes['filters'].firstChange) {
      this.filtersDiffer = this.keyValueDiffers.find(this.filters).create();
      this.initializeArrayDiffers();
      this.loadAllData();
    }
  }

  ngDoCheck(): void {
    if (this.filtersDiffer) {
      const changes = this.filtersDiffer.diff(this.filters);
      if (changes) {
        this.loadAllData();
        return;
      }
    }

    const arrayKeys = ['drinks', 'distributors', 'markets', 'clients', 'campuses', 'machines', 'products'];
    for (const key of arrayKeys) {
      const differ = this.arrayDiffers.get(key);
      if (differ) {
        const changes = differ.diff(this.filters[key]);
        if (changes) {
          this.loadAllData();
          return;
        }
      }
    }
  }

  private initializeArrayDiffers(): void {
    const arrayKeys = ['drinks', 'distributors', 'markets', 'clients', 'campuses', 'machines', 'products'];
    for (const key of arrayKeys) {
      this.arrayDiffers.set(key, this.iterableDiffers.find(this.filters[key]).create());
    }
  }

  private initializeColors(): void {
    this.drinkVolumeBarColors = this.getDefaultBarColors(false);
    this.machineDrinkBarColors = this.getDefaultBarColors(false);

    this.donutChartColors = [{
      backgroundColor: Array.from(ANALYTICS_COLORS.PRODUCT),
      hoverBackgroundColor: Array.from(ANALYTICS_COLORS.PRODUCT),
      borderColor: '#ffffff',
      borderWidth: 2
    }];
  }

  private getDefaultBarColors(hasCompareData: boolean): ChartColorConfig[] {
    if (hasCompareData) {
      return [
        {
          backgroundColor: ANALYTICS_COLORS.PRIMARY,
          borderColor: ANALYTICS_COLORS.PRIMARY
        },
        {
          backgroundColor: ANALYTICS_COLORS.PREVIOUS,
          borderColor: ANALYTICS_COLORS.PREVIOUS
        }
      ];
    }

    return [{
      backgroundColor: ANALYTICS_COLORS.PRIMARY,
      borderColor: ANALYTICS_COLORS.PRIMARY
    }];
  }

  loadAllData(): void {
    this.loadDrinkVolumeData();
    this.loadDrinkVolumeBreakdown();
    this.loadMachineDrinkVolumeData();
    this.loadMachineBreakdown();
  }

  private loadData<T>(
    loadingFlagRef: () => boolean,
    setLoadingFlag: (value: boolean) => void,
    httpCall: (params: Record<string, unknown>) => Observable<T>,
    onSuccess: (response: T) => void,
    onError?: () => void,
    modifyParams?: (params: Record<string, unknown>) => void
  ): void {
    if (loadingFlagRef()) {
      return;
    }

    const queryParams = this.buildQueryParams();
    if (!queryParams) {
      return;
    }

    setLoadingFlag(true);

    if (modifyParams) {
      modifyParams(queryParams);
    }

    if (this.filters.compareEnabled) {
      queryParams.compareEnabled = 'true';
    }

    httpCall(queryParams).subscribe(
      (response: T) => {
        setLoadingFlag(false);
        onSuccess(response);
      },
      (error: unknown) => {
        setLoadingFlag(false);
        if (onError) {
          onError();
        }
      }
    );
  }

  private areCustomDatesReady(): boolean {
    if (this.filters.datePreset !== 'custom') {
      return true;
    }
    return !!this.filters.startDate && !!this.filters.endDate;
  }

  private buildQueryParams(): Record<string, unknown> | null {
    if (!this.areCustomDatesReady()) {
      return null;
    }

    const params: Record<string, unknown> = {
      breakdownBy: this.filters.breakdownBy,
      datePreset: this.filters.datePreset,
      saleScope: this.filters.saleScope
    };

    if (this.filters.datePreset !== 'custom') {
      params.days = this.filters.days;
    } else {
      if (this.filters.startDate) {
        params.startDate = this.filters.startDate;
      }
      if (this.filters.endDate) {
        params.endDate = this.filters.endDate;
      }
    }

    if (this.filters.distributors.length > 0) {
      params.distributors = this.filters.distributors.join(',');
    }
    if (this.filters.markets.length > 0) {
      params.markets = this.filters.markets.join(',');
    }
    if (this.filters.clients.length > 0) {
      params.clients = this.filters.clients.join(',');
    }
    if (this.filters.campuses.length > 0) {
      params.campuses = this.filters.campuses.join(',');
    }
    if (this.filters.machines.length > 0) {
      params.machines = this.filters.machines.join(',');
    }
    if (this.filters.products.length > 0) {
      params.products = this.filters.products.join(',');
    }
    if (this.filters.drinks.length > 0) {
      params.drinks = this.filters.drinks.join(',');
    }

    if (this.filters.averageEnabled) {
      params.averageEnabled = 'true';
      params.avgBy = this.filters.avgBy;
    }

    return params;
  }

  loadDrinkVolumeData(): void {
    this.loadData(
      () => this.isLoadingDrinkVolume,
      (value) => { this.isLoadingDrinkVolume = value; },
      (params) => this.analyticsHttpService.getDrinkVolume(params),
      (response) => this.handleDrinkVolumeResponse(response)
    );
  }

  private parseApiResponse(response: unknown): { labels: string[]; values: number[]; compareData?: number[]; contextualData?: Record<string, { partner?: string; client?: string; campus?: string }>; total?: number; retailRevenue?: number; compareRetailRevenue?: number; retailRevenueData?: number[]; compareRetailRevenueData?: number[] } | null {
    if (!response || typeof response !== 'object') {
      return null;
    }

    const responseObj = response as Record<string, unknown>;
    const data = (responseObj.data && typeof responseObj.data === 'object' && !Array.isArray(responseObj.data))
      ? responseObj.data as Record<string, unknown>
      : responseObj;

    const labels = (data.labels as string[]) || [];
    const values = (data.data as number[]) || [];
    const compareData = (data.compareData as number[]) || undefined;
    const contextualData = (data.contextualData as Record<string, { partner?: string; client?: string; campus?: string }>) || undefined;
    const total = typeof data.total === 'number' ? data.total : undefined;
    const retailRevenue = typeof data.retailRevenue === 'number' ? data.retailRevenue : undefined;
    const compareRetailRevenue = typeof data.compareRetailRevenue === 'number' ? data.compareRetailRevenue : undefined;
    const retailRevenueData = Array.isArray(data.retailRevenueData) ? data.retailRevenueData as number[] : undefined;
    const compareRetailRevenueData = Array.isArray(data.compareRetailRevenueData) ? data.compareRetailRevenueData as number[] : undefined;

    if (!Array.isArray(labels) || !Array.isArray(values)) {
      return null;
    }

    return { labels, values, compareData, contextualData, total, retailRevenue, compareRetailRevenue, retailRevenueData, compareRetailRevenueData };
  }

  private createBarChartDatasets(values: number[], compareData?: number[]): ChartDataset[] {
    const currentBackgroundColor = new Array(values.length).fill(ANALYTICS_COLORS.PRIMARY);
    const currentBorderColor = new Array(values.length).fill(ANALYTICS_COLORS.PRIMARY);

    const currentDataset: ChartDataset = {
      label: 'Current',
      data: values.slice(),
      backgroundColor: currentBackgroundColor,
      borderColor: currentBorderColor,
      borderWidth: 0
    };

    const datasets: ChartDataset[] = [currentDataset];

    const hasCompareData = this.filters.compareEnabled &&
      compareData &&
      Array.isArray(compareData) &&
      compareData.length > 0;

    if (hasCompareData && compareData) {
      const previousBackgroundColor = new Array(compareData.length).fill(ANALYTICS_COLORS.PREVIOUS);
      const previousBorderColor = new Array(compareData.length).fill(ANALYTICS_COLORS.PREVIOUS);

      datasets.push({
        label: 'Previous',
        data: compareData.slice(),
        backgroundColor: previousBackgroundColor,
        borderColor: previousBorderColor,
        borderWidth: 0
      });
    } else {
      datasets[0].label = 'Total drinks';
    }

    return datasets;
  }

  private handleBarChartResponse(
    response: unknown,
    config: {
      labelsProperty: 'drinkVolumeBarChartLabels' | 'machineDrinkBarLabels';
      dataProperty: 'drinkVolumeBarChartData' | 'machineDrinkBarData';
      compareDataProperty: 'drinkVolumeCompareDataArray' | 'machineDrinkCompareDataArray';
      originalLabelsProperty: 'originalDrinkVolumeBarChartLabels' | 'originalMachineDrinkBarLabels';
      originalDataProperty: 'originalDrinkVolumeBarChartData' | 'originalMachineDrinkBarData';
      originalCompareProperty: 'originalDrinkVolumeCompareDataArray' | 'originalMachineDrinkCompareDataArray';
      fullLabelsProperty: 'fullDrinkVolumeBarChartLabels' | 'fullMachineDrinkBarLabels';
      fullDataProperty: 'fullDrinkVolumeBarChartData' | 'fullMachineDrinkBarData';
      fullCompareProperty: 'fullDrinkVolumeCompareDataArray' | 'fullMachineDrinkCompareDataArray';
      colorsProperty: 'drinkVolumeBarColors' | 'machineDrinkBarColors';
      sortMethod: () => void;
      updateMethod: () => void;
      emptyStateHandler?: () => void;
      totalProperty?: 'drinkVolumeApiTotal' | 'machineDrinkApiTotal';
    }
  ): void {
    const parsed = this.parseApiResponse(response);
    if (!parsed) {
      if (config.emptyStateHandler) {
        config.emptyStateHandler();
      }
      return;
    }

    const { labels, values, compareData, contextualData, total } = parsed;

    // Store the API total if provided
    if (config.totalProperty && typeof total === 'number') {
      (this[config.totalProperty] as number) = total;
    } else if (config.totalProperty) {
      // Reset to 0 if no total provided
      (this[config.totalProperty] as number) = 0;
    }

    // Populate contextual data from API response
    if (contextualData) {
      this.populateContextualData(labels, contextualData);
    }

    (this[config.labelsProperty] as string[]) = labels.slice();

    const hasCompareData = this.filters.compareEnabled &&
      compareData &&
      Array.isArray(compareData) &&
      compareData.length > 0;

    if (hasCompareData) {
      (this[config.compareDataProperty] as number[]) = compareData.slice();
    } else {
      (this[config.compareDataProperty] as number[]) = [];
    }

    const datasets = this.createBarChartDatasets(values, compareData);
    (this[config.dataProperty] as ChartDataset[]) = datasets;

    (this[config.originalLabelsProperty] as string[]) = labels.slice();
    (this[config.originalDataProperty] as ChartDataset[]) = datasets.map(dataset => ({
      ...dataset,
      data: (dataset.data as number[]).slice()
    }));
    (this[config.originalCompareProperty] as number[]) = hasCompareData && compareData
      ? compareData.slice()
      : [];

    config.sortMethod();

    (this[config.colorsProperty] as ChartColorConfig[]) = this.getDefaultBarColors(hasCompareData);

    config.updateMethod();
  }

  private handleDrinkVolumeResponse(response: unknown): void {
    this.drinkVolumeRetailRevenue = 0;
    this.drinkVolumeCompareRetailRevenue = 0;
    this.drinkVolumeRetailRevenueByLabel.clear();
    this.drinkVolumeCompareRetailRevenueByLabel.clear();

    const parsed = this.parseApiResponse(response);
    if (parsed) {
      this.drinkVolumeRetailRevenue = Math.round((parsed.retailRevenue || 0) * 100) / 100;
      this.drinkVolumeCompareRetailRevenue = Math.round((parsed.compareRetailRevenue || 0) * 100) / 100;

      if (parsed.retailRevenueData) {
        parsed.labels.forEach((label, index) => {
          this.drinkVolumeRetailRevenueByLabel.set(label, Math.round((parsed.retailRevenueData[index] || 0) * 100) / 100);
        });
      }

      if (parsed.compareRetailRevenueData) {
        parsed.labels.forEach((label, index) => {
          this.drinkVolumeCompareRetailRevenueByLabel.set(label, Math.round((parsed.compareRetailRevenueData[index] || 0) * 100) / 100);
        });
      }
    }

    this.handleBarChartResponse(response, {
      labelsProperty: 'drinkVolumeBarChartLabels',
      dataProperty: 'drinkVolumeBarChartData',
      compareDataProperty: 'drinkVolumeCompareDataArray',
      originalLabelsProperty: 'originalDrinkVolumeBarChartLabels',
      originalDataProperty: 'originalDrinkVolumeBarChartData',
      originalCompareProperty: 'originalDrinkVolumeCompareDataArray',
      fullLabelsProperty: 'fullDrinkVolumeBarChartLabels',
      fullDataProperty: 'fullDrinkVolumeBarChartData',
      fullCompareProperty: 'fullDrinkVolumeCompareDataArray',
      colorsProperty: 'drinkVolumeBarColors',
      sortMethod: () => this.sortDrinkVolumeBarChart(),
      updateMethod: () => this.forceUpdateDrinkVolumeChart(),
      totalProperty: 'drinkVolumeApiTotal'
    });

    this.syncDrinkVolumeChartDisplayData();
  }

  private forceUpdateDrinkVolumeChart(): void {
    this.chartUpdateService.recreateBarChart(
      this.cdr,
      () => ({
        labels: this.drinkVolumeChartDisplayLabels,
        datasets: this.drinkVolumeChartDisplayData,
        compareData: this.drinkVolumeChartDisplayCompareData
      }),
      (data) => {
        this.drinkVolumeChartDisplayLabels = data.labels;
        this.drinkVolumeChartDisplayData = data.datasets;
        this.drinkVolumeChartDisplayCompareData = data.compareData;
      },
      'DrinkVolumeBarChart'
    );
  }

  private syncDrinkVolumeChartDisplayData(): void {
    if (this.filters.saleScope === 'retail' && this.lastObservedSaleScope !== 'retail') {
      this.chartMetricMode = 'revenue';
    }

    if (!this.shouldShowBarChartMetricToggle() && this.chartMetricMode !== 'volume') {
      this.chartMetricMode = 'volume';
    }

    this.lastObservedSaleScope = this.filters.saleScope;

    this.drinkVolumeChartDisplayLabels = this.drinkVolumeBarChartLabels.slice();

    if (!this.isRevenueBarChartMode()) {
      this.drinkVolumeChartDisplayData = this.drinkVolumeBarChartData.map(dataset => ({
        ...dataset,
        data: Array.isArray(dataset.data) ? dataset.data.slice() : []
      }));
      this.drinkVolumeChartDisplayCompareData = this.drinkVolumeCompareDataArray.slice();
      this.drinkVolumeBarColors = this.getDefaultBarColors(this.filters.compareEnabled && this.drinkVolumeCompareDataArray.length > 0);
      this.applyDrinkVolumeBarChartMetricFormatting();
      return;
    }

    const labels = this.drinkVolumeChartDisplayLabels;
    const currentRevenue = labels.map(label => Math.round((this.drinkVolumeRetailRevenueByLabel.get(label) || 0) * 100) / 100);
    const compareRevenue = this.filters.compareEnabled
      ? labels.map(label => Math.round((this.drinkVolumeCompareRetailRevenueByLabel.get(label) || 0) * 100) / 100)
      : [];

    const datasets: ChartDataset[] = [
      {
        label: this.filters.compareEnabled ? 'Current revenue' : 'Retail revenue',
        data: currentRevenue,
        backgroundColor: new Array(currentRevenue.length).fill(this.revenueChartGreen),
        borderColor: new Array(currentRevenue.length).fill(this.revenueChartGreen),
        borderWidth: 0
      }
    ];

    if (this.filters.compareEnabled) {
      datasets.push({
        label: 'Previous revenue',
        data: compareRevenue,
        backgroundColor: new Array(compareRevenue.length).fill(ANALYTICS_COLORS.PREVIOUS),
        borderColor: new Array(compareRevenue.length).fill(ANALYTICS_COLORS.PREVIOUS),
        borderWidth: 0
      });
    }

    this.drinkVolumeChartDisplayData = datasets;
    this.drinkVolumeChartDisplayCompareData = compareRevenue;
    this.drinkVolumeBarColors = this.filters.compareEnabled
      ? [
          {
            backgroundColor: this.revenueChartGreen,
            borderColor: this.revenueChartGreen
          },
          {
            backgroundColor: ANALYTICS_COLORS.PREVIOUS,
            borderColor: ANALYTICS_COLORS.PREVIOUS
          }
        ]
      : [
          {
            backgroundColor: this.revenueChartGreen,
            borderColor: this.revenueChartGreen
          }
        ];
    this.applyDrinkVolumeBarChartMetricFormatting();
  }

  private formatCurrencyValue(value: number, withCents: boolean = true): string {
    const safeValue = Number.isFinite(value) ? value : 0;
    return safeValue.toLocaleString('en-US', {
      minimumFractionDigits: withCents ? 2 : 0,
      maximumFractionDigits: withCents ? 2 : 0
    });
  }

  private applyDrinkVolumeBarChartMetricFormatting(): void {
    const nextOptions = getBarChartOptions() as any;

    if (this.isRevenueBarChartMode()) {
      const yAxes = nextOptions.scales && nextOptions.scales.yAxes ? nextOptions.scales.yAxes : [];
      if (yAxes[0] && yAxes[0].ticks) {
        yAxes[0].ticks.callback = (value: number) => `$${this.formatCurrencyValue(Number(value), false)}`;
      }

      nextOptions.tooltips = {
        ...(nextOptions.tooltips || {}),
        callbacks: {
          label: (tooltipItem: any, data: any) => {
            const datasetLabel = data && data.datasets && data.datasets[tooltipItem.datasetIndex]
              ? data.datasets[tooltipItem.datasetIndex].label || ''
              : '';
            const value = Number(tooltipItem.yLabel || 0);
            const prefix = datasetLabel ? `${datasetLabel}: ` : '';
            return `${prefix}$${this.formatCurrencyValue(value, true)}`;
          }
        }
      };
    }

    this.drinkVolumeBarChartOptions = nextOptions;
  }

  loadDrinkVolumeBreakdown(): void {
    this.loadData(
      () => this.isLoadingDrinkVolumeBreakdown,
      (value) => { this.isLoadingDrinkVolumeBreakdown = value; },
      (params) => this.analyticsHttpService.getDrinkVolumeBreakdown(params),
      (response) => this.handleDrinkVolumeBreakdownResponse(response),
      () => {
        this.drinkVolumeBreakdownDonutLabels = [];
        this.drinkVolumeBreakdownDonutData = [];
        this.breakdownDonutChartOptions = getDoughnutChartOptions(false);
        this.recreateBreakdownDonutChart();
      },
      (params) => { delete params.breakdownBy; }
    );
  }

  private handleDonutChartResponse(
    response: unknown,
    config: {
      labelsProperty: 'drinkVolumeBreakdownDonutLabels' | 'machineDonutLabels';
      dataProperty: 'drinkVolumeBreakdownDonutData' | 'machineDonutData';
      optionsProperty: 'breakdownDonutChartOptions' | 'machineDonutChartOptions';
      recreateMethod: () => void;
    }
  ): void {
    const parsed = this.parseApiResponse(response);
    if (!parsed) {
      (this[config.labelsProperty] as string[]) = [];
      (this[config.dataProperty] as ChartDataset[]) = [];
      (this[config.optionsProperty] as unknown) = getDoughnutChartOptions(false);
      config.recreateMethod();
      return;
    }

    const { labels, values, compareData } = parsed;

    const hasCompareData = this.filters.compareEnabled &&
      compareData &&
      Array.isArray(compareData) &&
      compareData.length > 0;

    if (hasCompareData && compareData) {
      const concentricData = createConcentricDonutDatasets(
        labels,
        values,
        labels,
        compareData,
        Array.from(ANALYTICS_COLORS.PRODUCT)
      );
      (this[config.labelsProperty] as string[]) = concentricData.labels;
      (this[config.dataProperty] as ChartDataset[]) = concentricData.datasets;
    } else {
      const transformed = createSingleDonutDataset(
        labels,
        values,
        Array.from(ANALYTICS_COLORS.PRODUCT)
      );
      (this[config.labelsProperty] as string[]) = transformed.labels;
      (this[config.dataProperty] as ChartDataset[]) = transformed.datasets;
    }

    const hasCompare = this.filters.compareEnabled && (this[config.dataProperty] as ChartDataset[]).length > 1;
    (this[config.optionsProperty] as unknown) = getDoughnutChartOptions(hasCompare);

    config.recreateMethod();
  }

  private handleDrinkVolumeBreakdownResponse(response: unknown): void {
    this.handleDonutChartResponse(response, {
      labelsProperty: 'drinkVolumeBreakdownDonutLabels',
      dataProperty: 'drinkVolumeBreakdownDonutData',
      optionsProperty: 'breakdownDonutChartOptions',
      recreateMethod: () => this.recreateBreakdownDonutChart()
    });
  }

  private recreateBreakdownDonutChart(): void {
    this.isDestroyingCharts = true;

    if (this.drinkVolumeBreakdownDonutChart && this.drinkVolumeBreakdownDonutChart.chart) {
      this.drinkVolumeBreakdownDonutChart.chart.destroy();
      (this.drinkVolumeBreakdownDonutChart as any).chart = null;
    }

    this.breakdownDonutChartKey = 0;
    this.cdr.markForCheck();

    setTimeout(() => {
      this.breakdownDonutChartKey++;
      this.cdr.markForCheck();
      this.isDestroyingCharts = false;
      setTimeout(() => {
        this.chartUpdateService.updateDonutChart(
          this.cdr,
          {
            chartInstance: this.drinkVolumeBreakdownDonutChart,
            chartKey: this.breakdownDonutChartKey,
            labels: this.drinkVolumeBreakdownDonutLabels,
            datasets: this.drinkVolumeBreakdownDonutData,
            chartOptions: this.breakdownDonutChartOptions,
            chartId: 'drinkVolumeBreakdownDonutChart',
            activeTab: 'drink-volume',
            requiredTab: 'drink-volume',
            isDestroyingCharts: this.isDestroyingCharts
          }
        );
      }, CHART_UPDATE_DELAY);
    }, 50);
  }

  loadMachineDrinkVolumeData(): void {
    this.loadData(
      () => this.isLoadingMachineDrinkVolume,
      (value) => { this.isLoadingMachineDrinkVolume = value; },
      (params) => this.analyticsHttpService.getDrinkVolumeByMachine(params),
      (response) => this.handleMachineDrinkVolumeResponse(response),
      () => {
        this.machineDrinkBarLabels = [];
        this.machineDrinkBarData = [{
          label: 'Total drinks',
          data: [],
          borderWidth: 0
        }];
        this.machineDrinkCompareDataArray = [];
        this.forceUpdateMachineDrinkBarChart();
      },
      (params) => { delete params.breakdownBy; }
    );
  }

  private handleMachineDrinkVolumeResponse(response: unknown): void {
    this.handleBarChartResponse(response, {
      labelsProperty: 'machineDrinkBarLabels',
      dataProperty: 'machineDrinkBarData',
      compareDataProperty: 'machineDrinkCompareDataArray',
      originalLabelsProperty: 'originalMachineDrinkBarLabels',
      originalDataProperty: 'originalMachineDrinkBarData',
      originalCompareProperty: 'originalMachineDrinkCompareDataArray',
      fullLabelsProperty: 'fullMachineDrinkBarLabels',
      fullDataProperty: 'fullMachineDrinkBarData',
      fullCompareProperty: 'fullMachineDrinkCompareDataArray',
      colorsProperty: 'machineDrinkBarColors',
      sortMethod: () => this.sortMachineDrinkBarChart(),
      updateMethod: () => this.forceUpdateMachineDrinkBarChart(),
      emptyStateHandler: () => {
        this.machineDrinkBarLabels = [];
        this.machineDrinkBarData = [{
          label: 'Total drinks',
          data: [],
          borderWidth: 0
        }];
        this.machineDrinkCompareDataArray = [];
        this.forceUpdateMachineDrinkBarChart();
      },
      totalProperty: 'machineDrinkApiTotal'
    });
  }

  private forceUpdateMachineDrinkBarChart(): void {
    this.chartUpdateService.recreateBarChart(
      this.cdr,
      () => ({
        labels: this.machineDrinkBarLabels,
        datasets: this.machineDrinkBarData,
        compareData: this.machineDrinkCompareDataArray
      }),
      (data) => {
        this.machineDrinkBarLabels = data.labels;
        this.machineDrinkBarData = data.datasets;
        this.machineDrinkCompareDataArray = data.compareData;
      },
      'MachineDrinkBarChart'
    );
  }

  loadMachineBreakdown(): void {
    this.loadData(
      () => this.isLoadingMachineBreakdown,
      (value) => { this.isLoadingMachineBreakdown = value; },
      (params) => this.analyticsHttpService.getDrinkVolumeMachineBreakdown(params),
      (response) => this.handleMachineBreakdownResponse(response),
      () => {
        this.machineDonutLabels = [];
        this.machineDonutData = [];
        this.machineDonutChartOptions = getDoughnutChartOptions(false);
        this.recreateMachineDonutChart();
      },
      (params) => { delete params.breakdownBy; }
    );
  }

  private handleMachineBreakdownResponse(response: unknown): void {
    this.handleDonutChartResponse(response, {
      labelsProperty: 'machineDonutLabels',
      dataProperty: 'machineDonutData',
      optionsProperty: 'machineDonutChartOptions',
      recreateMethod: () => this.recreateMachineDonutChart()
    });
  }

  private recreateMachineDonutChart(): void {
    this.isDestroyingCharts = true;

    if (this.machineDonutChart && this.machineDonutChart.chart) {
      this.machineDonutChart.chart.destroy();
      (this.machineDonutChart as any).chart = null;
    }

    this.machineDonutChartKey = 0;
    this.cdr.markForCheck();

    setTimeout(() => {
      this.machineDonutChartKey++;
      this.cdr.markForCheck();
      this.isDestroyingCharts = false;
      setTimeout(() => {
        this.chartUpdateService.updateDonutChart(
          this.cdr,
          {
            chartInstance: this.machineDonutChart,
            chartKey: this.machineDonutChartKey,
            labels: this.machineDonutLabels,
            datasets: this.machineDonutData,
            chartOptions: this.machineDonutChartOptions,
            chartId: 'machineDonutChart',
            activeTab: 'drink-volume',
            requiredTab: 'drink-volume',
            isDestroyingCharts: this.isDestroyingCharts
          }
        );
      }, CHART_UPDATE_DELAY);
    }, 50);
  }

  toggleDrinkVolumeBarChartSort(): void {
    this.drinkVolumeBarChartSort = this.drinkVolumeBarChartSort === 'desc' ? 'asc' : 'desc';
    this.sortDrinkVolumeBarChart();
    this.forceUpdateDrinkVolumeChart();
  }

  private sortDrinkVolumeBarChart(): void {
    const labelsToSort = this.originalDrinkVolumeBarChartLabels.length > 0
      ? this.originalDrinkVolumeBarChartLabels
      : this.drinkVolumeBarChartLabels;
    const dataToSort = this.originalDrinkVolumeBarChartData.length > 0
      ? this.originalDrinkVolumeBarChartData
      : this.drinkVolumeBarChartData;
    const compareDataToSort = this.originalDrinkVolumeCompareDataArray.length > 0
      ? this.originalDrinkVolumeCompareDataArray
      : this.drinkVolumeCompareDataArray;

    const result = sortBarChartData(
      labelsToSort,
      dataToSort,
      compareDataToSort.length > 0 ? compareDataToSort : undefined,
      this.drinkVolumeBarChartSort,
      this.filters.compareEnabled
    );
    this.fullDrinkVolumeBarChartLabels = result.labels;
    this.fullDrinkVolumeBarChartData = result.datasets;
    this.fullDrinkVolumeCompareDataArray = result.compareData;

    this.updateDrinkVolumeDisplayData();
  }

  private updateDrinkVolumeDisplayData(): void {
    const limit = this.showAllDrinkVolumeItems
      ? this.fullDrinkVolumeBarChartLabels.length
      : DEFAULT_CHART_ITEM_LIMIT;

    const result = limitBarChartData(
      this.fullDrinkVolumeBarChartLabels,
      this.fullDrinkVolumeBarChartData,
      this.fullDrinkVolumeCompareDataArray.length > 0 ? this.fullDrinkVolumeCompareDataArray : undefined,
      limit,
      false
    );

    this.drinkVolumeBarChartLabels = result.labels;
    this.drinkVolumeBarChartData = result.datasets;
    this.drinkVolumeCompareDataArray = result.compareData;
    this.syncDrinkVolumeChartDisplayData();
  }

  toggleShowAllDrinkVolumeItems(): void {
    this.showAllDrinkVolumeItems = !this.showAllDrinkVolumeItems;
    this.updateDrinkVolumeDisplayData();
    this.forceUpdateDrinkVolumeChart();
  }

  shouldShowDrinkVolumeToggle(): boolean {
    return this.fullDrinkVolumeBarChartLabels.length > DEFAULT_CHART_ITEM_LIMIT;
  }

  getDrinkVolumeToggleTitle(): string {
    if (this.showAllDrinkVolumeItems) {
      return this.drinkVolumeBarChartSort === 'desc' ? 'Show top 10' : 'Show bottom 10';
    }
    return 'Show all';
  }

  toggleMachineDrinkBarChartSort(): void {
    this.machineDrinkBarChartSort = this.machineDrinkBarChartSort === 'desc' ? 'asc' : 'desc';
    this.sortMachineDrinkBarChart();
    this.forceUpdateMachineDrinkChart();
  }

  private sortMachineDrinkBarChart(): void {
    const labelsToSort = this.originalMachineDrinkBarLabels.length > 0
      ? this.originalMachineDrinkBarLabels
      : this.machineDrinkBarLabels;
    const dataToSort = this.originalMachineDrinkBarData.length > 0
      ? this.originalMachineDrinkBarData
      : this.machineDrinkBarData;
    const compareDataToSort = this.originalMachineDrinkCompareDataArray.length > 0
      ? this.originalMachineDrinkCompareDataArray
      : this.machineDrinkCompareDataArray;

    const result = sortBarChartData(
      labelsToSort,
      dataToSort,
      compareDataToSort.length > 0 ? compareDataToSort : undefined,
      this.machineDrinkBarChartSort,
      this.filters.compareEnabled
    );
    this.fullMachineDrinkBarLabels = result.labels;
    this.fullMachineDrinkBarData = result.datasets;
    this.fullMachineDrinkCompareDataArray = result.compareData;

    this.updateMachineDrinkDisplayData();
  }

  private updateMachineDrinkDisplayData(): void {
    const limit = this.showAllMachineDrinkItems
      ? this.fullMachineDrinkBarLabels.length
      : DEFAULT_CHART_ITEM_LIMIT;

    const result = limitBarChartData(
      this.fullMachineDrinkBarLabels,
      this.fullMachineDrinkBarData,
      this.fullMachineDrinkCompareDataArray.length > 0 ? this.fullMachineDrinkCompareDataArray : undefined,
      limit,
      false
    );

    this.machineDrinkBarLabels = result.labels;
    this.machineDrinkBarData = result.datasets;
    this.machineDrinkCompareDataArray = result.compareData;
  }

  toggleShowAllMachineDrinkItems(): void {
    this.showAllMachineDrinkItems = !this.showAllMachineDrinkItems;
    this.updateMachineDrinkDisplayData();
    this.forceUpdateMachineDrinkChart();
  }

  private forceUpdateMachineDrinkChart(): void {
    this.chartUpdateService.recreateBarChart(
      this.cdr,
      () => ({
        labels: this.machineDrinkBarLabels,
        datasets: this.machineDrinkBarData,
        compareData: this.machineDrinkCompareDataArray
      }),
      (data) => {
        this.machineDrinkBarLabels = data.labels;
        this.machineDrinkBarData = data.datasets;
        this.machineDrinkCompareDataArray = data.compareData;
      },
      'MachineDrinkBarChart'
    );
  }

  shouldShowMachineDrinkToggle(): boolean {
    return this.fullMachineDrinkBarLabels.length > DEFAULT_CHART_ITEM_LIMIT;
  }

  getMachineDrinkToggleTitle(): string {
    if (this.showAllMachineDrinkItems) {
      return this.machineDrinkBarChartSort === 'desc' ? 'Show top 10' : 'Show bottom 10';
    }
    return 'Show all';
  }

  getDrinkVolumeBarChartTotal(): number {
    // Use API total if available, otherwise calculate from full data
    if (this.drinkVolumeApiTotal > 0) {
      return this.drinkVolumeApiTotal;
    }
    // Try full sorted data first, then original unsorted data
    if (this.fullDrinkVolumeBarChartData.length > 0) {
      return calculateFirstDatasetTotal(this.fullDrinkVolumeBarChartData);
    }
    if (this.originalDrinkVolumeBarChartData.length > 0) {
      return calculateFirstDatasetTotal(this.originalDrinkVolumeBarChartData);
    }
    return calculateFirstDatasetTotal(this.drinkVolumeBarChartData);
  }

  getDrinkVolumeCompareTotal(): number {
    // Calculate from full compare data array, fallback to original, then limited
    if (this.fullDrinkVolumeCompareDataArray.length > 0) {
      return calculateArrayTotal(this.fullDrinkVolumeCompareDataArray);
    }
    if (this.originalDrinkVolumeCompareDataArray.length > 0) {
      return calculateArrayTotal(this.originalDrinkVolumeCompareDataArray);
    }
    return calculateArrayTotal(this.drinkVolumeCompareDataArray);
  }

  getMachineDrinkBarTotal(): number {
    // Use API total if available, otherwise calculate from full data
    if (this.machineDrinkApiTotal > 0) {
      return this.machineDrinkApiTotal;
    }
    // Try full sorted data first, then original unsorted data
    if (this.fullMachineDrinkBarData.length > 0) {
      return calculateFirstDatasetTotal(this.fullMachineDrinkBarData);
    }
    if (this.originalMachineDrinkBarData.length > 0) {
      return calculateFirstDatasetTotal(this.originalMachineDrinkBarData);
    }
    return calculateFirstDatasetTotal(this.machineDrinkBarData);
  }

  getMachineDrinkCompareTotal(): number {
    // Calculate from full compare data array, fallback to original, then limited
    if (this.fullMachineDrinkCompareDataArray.length > 0) {
      return calculateArrayTotal(this.fullMachineDrinkCompareDataArray);
    }
    if (this.originalMachineDrinkCompareDataArray.length > 0) {
      return calculateArrayTotal(this.originalMachineDrinkCompareDataArray);
    }
    return calculateArrayTotal(this.machineDrinkCompareDataArray);
  }

  // Helper methods to get full data for tables
  getFullDrinkVolumeLabels(): string[] {
    // Prefer full sorted arrays, then original unsorted arrays, then limited display arrays
    if (this.fullDrinkVolumeBarChartLabels.length > 0) {
      return this.fullDrinkVolumeBarChartLabels;
    }
    if (this.originalDrinkVolumeBarChartLabels.length > 0) {
      return this.originalDrinkVolumeBarChartLabels;
    }
    return this.drinkVolumeBarChartLabels;
  }

  getFullDrinkVolumeData(): ChartDataset[] {
    // Prefer full sorted arrays, then original unsorted arrays, then limited display arrays
    if (this.fullDrinkVolumeBarChartData.length > 0) {
      return this.fullDrinkVolumeBarChartData;
    }
    if (this.originalDrinkVolumeBarChartData.length > 0) {
      return this.originalDrinkVolumeBarChartData;
    }
    return this.drinkVolumeBarChartData;
  }

  getFullDrinkVolumeCompareData(): number[] {
    // Prefer full sorted arrays, then original unsorted arrays, then limited display arrays
    if (this.fullDrinkVolumeCompareDataArray.length > 0) {
      return this.fullDrinkVolumeCompareDataArray;
    }
    if (this.originalDrinkVolumeCompareDataArray.length > 0) {
      return this.originalDrinkVolumeCompareDataArray;
    }
    return this.drinkVolumeCompareDataArray;
  }

  getFullMachineDrinkLabels(): string[] {
    // Prefer full sorted arrays, then original unsorted arrays, then limited display arrays
    if (this.fullMachineDrinkBarLabels.length > 0) {
      return this.fullMachineDrinkBarLabels;
    }
    if (this.originalMachineDrinkBarLabels.length > 0) {
      return this.originalMachineDrinkBarLabels;
    }
    return this.machineDrinkBarLabels;
  }

  getFullMachineDrinkData(): ChartDataset[] {
    // Prefer full sorted arrays, then original unsorted arrays, then limited display arrays
    if (this.fullMachineDrinkBarData.length > 0) {
      return this.fullMachineDrinkBarData;
    }
    if (this.originalMachineDrinkBarData.length > 0) {
      return this.originalMachineDrinkBarData;
    }
    return this.machineDrinkBarData;
  }

  getFullMachineDrinkCompareData(): number[] {
    // Prefer full sorted arrays, then original unsorted arrays, then limited display arrays
    if (this.fullMachineDrinkCompareDataArray.length > 0) {
      return this.fullMachineDrinkCompareDataArray;
    }
    if (this.originalMachineDrinkCompareDataArray.length > 0) {
      return this.originalMachineDrinkCompareDataArray;
    }
    return this.machineDrinkCompareDataArray;
  }

  // Helper methods to check if full data has items
  hasFullDrinkVolumeData(): boolean {
    const labels = this.getFullDrinkVolumeLabels();
    const data = this.getFullDrinkVolumeData();
    return labels.length > 0 && data.length > 0 && data[0] && Array.isArray(data[0].data) && data[0].data.length > 0;
  }

  hasFullMachineDrinkData(): boolean {
    const labels = this.getFullMachineDrinkLabels();
    const data = this.getFullMachineDrinkData();
    return labels.length > 0 && data.length > 0 && data[0] && Array.isArray(data[0].data) && data[0].data.length > 0;
  }

  getTableFirstColumnHeader(): string {
    return getBreakdownDisplayName(this.filters.breakdownBy);
  }

  getTableSecondColumnHeader(): string {
    if (this.filters.averageEnabled) {
      const avgLabel = this.filters.avgBy === 'day' ? 'Day' :
        this.filters.avgBy === 'week' ? 'Week' : 'Month';
      return `Average per ${avgLabel}`;
    }
    return 'Total';
  }

  getTableThirdColumnHeader(): string {
    if (!this.filters.compareEnabled) {
      return '';
    }
    if (this.filters.averageEnabled) {
      const avgLabel = this.filters.avgBy === 'day' ? 'Day' :
        this.filters.avgBy === 'week' ? 'Week' : 'Month';
      return `Previous Average per ${avgLabel}`;
    }
    return 'Previous Total';
  }

  shouldShowRetailRevenueColumns(): boolean {
    return this.filters.saleScope === 'retail';
  }

  getRetailRevenueByLabel(label: string): number {
    return this.drinkVolumeRetailRevenueByLabel.get(label) || 0;
  }

  getCompareRetailRevenueByLabel(label: string): number {
    return this.drinkVolumeCompareRetailRevenueByLabel.get(label) || 0;
  }

  getRetailRevenueHeader(): string {
    if (!this.filters.averageEnabled) {
      return 'Retail Revenue';
    }

    const avgLabel = this.filters.avgBy === 'day' ? 'Day' :
      this.filters.avgBy === 'week' ? 'Week' : 'Month';
    return `Average Retail Revenue per ${avgLabel}`;
  }

  getCompareRetailRevenueHeader(): string {
    if (!this.filters.averageEnabled) {
      return 'Previous Retail Revenue';
    }

    const avgLabel = this.filters.avgBy === 'day' ? 'Day' :
      this.filters.avgBy === 'week' ? 'Week' : 'Month';
    return `Previous Average Retail Revenue per ${avgLabel}`;
  }

  shouldShowBarChartMetricToggle(): boolean {
    return this.filters.saleScope === 'retail';
  }

  setBarChartMetricMode(value: 'volume' | 'revenue'): void {
    this.chartMetricMode = value;
    this.syncDrinkVolumeChartDisplayData();
    this.forceUpdateDrinkVolumeChart();
  }

  getDrinkVolumeBarChartTitle(): string {
    return this.isRevenueBarChartMode() ? 'Revenue by Drink' : 'Volume by Drink';
  }

  private isRevenueBarChartMode(): boolean {
    return this.shouldShowBarChartMetricToggle() && this.chartMetricMode === 'revenue';
  }

  truncateLabel(label: string): string {
    return truncateLabelUtil(label);
  }

  hasNoData(labels: string[], datasets: ChartDataset[]): boolean {
    return labels.length === 0 || datasets.length === 0 || (datasets.length > 0 && datasets[0].data.length === 0);
  }

  /**
   * Get tooltip text for a label based on breakdown type
   */
  getContextualTooltip(label: string): string {
    const breakdownBy = this.filters.breakdownBy;
    const context = this.contextualDataMap.get(label);

    if (!context) {
      return '';
    }

    if (breakdownBy === 'client') {
      // Show Partner
      return context.partner ? `Partner: ${context.partner}` : '';
    } else if (breakdownBy === 'campus') {
      // Show Partner first, then Client
      const parts: string[] = [];
      if (context.partner) {
        parts.push(`Partner: ${context.partner}`);
      }
      if (context.client) {
        parts.push(`Client: ${context.client}`);
      }
      return parts.join('\n');
    } else if (breakdownBy === 'machine') {
      // Show Partner first, then Client, then Campus
      const parts: string[] = [];
      if (context.partner) {
        parts.push(`Partner: ${context.partner}`);
      }
      if (context.client) {
        parts.push(`Client: ${context.client}`);
      }
      if (context.campus) {
        parts.push(`Campus: ${context.campus}`);
      }
      return parts.join('\n');
    }

    return '';
  }

  /**
   * Check if tooltip should be shown based on breakdown type
   */
  shouldShowContextualTooltip(): boolean {
    const breakdownBy = this.filters.breakdownBy;
    return breakdownBy === 'client' || breakdownBy === 'campus' || breakdownBy === 'machine';
  }

  /**
   * Show custom tooltip on mouse enter
   */
  onTooltipMouseEnter(event: MouseEvent, label: string): void {
    if (!this.shouldShowContextualTooltip()) {
      return;
    }
    const tooltipText = this.getContextualTooltip(label);
    if (!tooltipText) {
      return;
    }
    this.tooltipVisible = true;
    this.tooltipText = tooltipText;
    // Set position once on enter - tooltip stays static
    this.tooltipX = event.clientX;
    this.tooltipY = event.clientY - 10; // 10px above cursor
  }

  /**
   * Hide tooltip on mouse leave
   */
  onTooltipMouseLeave(): void {
    this.tooltipVisible = false;
  }

  /**
   * Populate contextual data from API response
   */
  private populateContextualData(labels: string[], contextualData: Record<string, { partner?: string; client?: string; campus?: string }>): void {
    const breakdownBy = this.filters.breakdownBy;

    // Only populate for client, campus, or machine breakdowns
    if (breakdownBy !== 'client' && breakdownBy !== 'campus' && breakdownBy !== 'machine') {
      return;
    }

    // Clear existing contextual data for these labels
    labels.forEach(label => {
      this.contextualDataMap.delete(label);
    });

    // Populate from API response
    labels.forEach((label) => {
      const contextData = contextualData[label];
      if (contextData) {
        const context: { partner?: string; client?: string; campus?: string } = {};

        if (contextData.partner) {
          context.partner = contextData.partner;
        }
        if (contextData.client) {
          context.client = contextData.client;
        }
        if (contextData.campus) {
          context.campus = contextData.campus;
        }

        // Only set if we have at least one property
        if (context.partner || context.client || context.campus) {
          this.contextualDataMap.set(label, context);
        }
      }
    });
  }

  /**
   * Check if data is available for export
   * Check both full and limited arrays to handle all breakdown scenarios
   */
  hasData(): boolean {
    // Check limited arrays (what's currently displayed)
    const hasLimitedData = this.drinkVolumeBarChartLabels.length > 0 &&
           this.drinkVolumeBarChartData.length > 0 &&
           (this.drinkVolumeBarChartData[0] && this.drinkVolumeBarChartData[0].data && this.drinkVolumeBarChartData[0].data.length > 0);

    // Check full arrays (all data, including when "show all" is available)
    const hasFullData = this.fullDrinkVolumeBarChartLabels.length > 0 &&
           this.fullDrinkVolumeBarChartData.length > 0 &&
           (this.fullDrinkVolumeBarChartData[0] && this.fullDrinkVolumeBarChartData[0].data && this.fullDrinkVolumeBarChartData[0].data.length > 0);

    return hasLimitedData || hasFullData;
  }

  /**
   * Export drink volume table to Excel
   */
  exportTable(): void {
    console.log('[Drink Volume Export] exportTable() called');
    console.log('[Drink Volume Export] Current state:', {
      fullLabelsLength: this.fullDrinkVolumeBarChartLabels.length,
      labelsLength: this.drinkVolumeBarChartLabels.length,
      fullDataLength: this.fullDrinkVolumeBarChartData.length,
      dataLength: this.drinkVolumeBarChartData.length,
      fullDataFirstItem: this.fullDrinkVolumeBarChartData[0] ? {
        hasData: !!this.fullDrinkVolumeBarChartData[0].data,
        dataLength: Array.isArray(this.fullDrinkVolumeBarChartData[0].data) ? this.fullDrinkVolumeBarChartData[0].data.length : 0
      } : null,
      dataFirstItem: this.drinkVolumeBarChartData[0] ? {
        hasData: !!this.drinkVolumeBarChartData[0].data,
        dataLength: Array.isArray(this.drinkVolumeBarChartData[0].data) ? this.drinkVolumeBarChartData[0].data.length : 0
      } : null,
      filters: this.filters
    });

    try {
      // Use full arrays for export (all data, not just visible)
      // Fallback to limited arrays if full arrays are empty
      const labels = this.fullDrinkVolumeBarChartLabels.length > 0
        ? this.fullDrinkVolumeBarChartLabels
        : this.drinkVolumeBarChartLabels;

      console.log('[Drink Volume Export] Selected labels array:', {
        source: this.fullDrinkVolumeBarChartLabels.length > 0 ? 'full' : 'limited',
        length: labels.length,
        labels: labels.slice(0, 5) // Log first 5 labels
      });

      // Safely extract data array, handling single-row cases
      let data: number[] = [];
      if (this.fullDrinkVolumeBarChartData.length > 0 &&
          this.fullDrinkVolumeBarChartData[0] &&
          this.fullDrinkVolumeBarChartData[0].data &&
          Array.isArray(this.fullDrinkVolumeBarChartData[0].data) &&
          this.fullDrinkVolumeBarChartData[0].data.length > 0) {
        data = this.fullDrinkVolumeBarChartData[0].data;
        console.log('[Drink Volume Export] Using full data array, length:', data.length);
      } else if (this.drinkVolumeBarChartData.length > 0 &&
                 this.drinkVolumeBarChartData[0] &&
                 this.drinkVolumeBarChartData[0].data &&
                 Array.isArray(this.drinkVolumeBarChartData[0].data)) {
        data = this.drinkVolumeBarChartData[0].data;
        console.log('[Drink Volume Export] Using limited data array, length:', data.length);
      } else {
        console.warn('[Drink Volume Export] No valid data array found');
      }

      const compareData = this.filters.compareEnabled && this.fullDrinkVolumeCompareDataArray.length > 0
        ? this.fullDrinkVolumeCompareDataArray
        : (this.filters.compareEnabled && this.drinkVolumeCompareDataArray.length > 0
           ? this.drinkVolumeCompareDataArray
           : undefined);

      const retailRevenueData = labels.map(label => this.getRetailRevenueByLabel(label));
      const compareRetailRevenueData = this.filters.compareEnabled
        ? labels.map(label => this.getCompareRetailRevenueByLabel(label))
        : undefined;

      console.log('[Drink Volume Export] Compare data:', {
        enabled: this.filters.compareEnabled,
        hasCompareData: !!compareData,
        compareDataLength: compareData ? compareData.length : 0
      });

      // Ensure labels and data arrays have matching lengths
      if (labels.length === 0 || data.length === 0) {
        console.error('[Drink Volume Export] No data available for export:', {
          labelsLength: labels.length,
          dataLength: data.length
        });
        // Throw error so parent component can show user-facing message
        throw new Error('No data available for export. Please ensure data has loaded.');
      }

      if (labels.length !== data.length) {
        console.warn('[Drink Volume Export] Mismatch between labels and data length:', {
          labelsLength: labels.length,
          dataLength: data.length
        });
      }

      const exportData = {
        labels: labels,
        data: data,
        compareData: compareData,
        retailRevenueData: retailRevenueData,
        compareRetailRevenueData: compareRetailRevenueData,
        retailRevenueHeader: this.getRetailRevenueHeader(),
        compareRetailRevenueHeader: this.filters.compareEnabled ? this.getCompareRetailRevenueHeader() : undefined,
        firstColumnHeader: this.getTableFirstColumnHeader(),
        secondColumnHeader: this.getTableSecondColumnHeader(),
        thirdColumnHeader: this.filters.compareEnabled ? this.getTableThirdColumnHeader() : undefined,
        total: this.getDrinkVolumeBarChartTotal(),
        compareTotal: this.filters.compareEnabled ? this.getDrinkVolumeCompareTotal() : undefined,
        retailRevenueTotal: this.drinkVolumeRetailRevenue,
        compareRetailRevenueTotal: this.filters.compareEnabled ? this.drinkVolumeCompareRetailRevenue : undefined
      };

      console.log('[Drink Volume Export] Calling export service with data:', {
        labelsCount: exportData.labels.length,
        dataCount: exportData.data.length,
        hasCompareData: !!exportData.compareData,
        firstColumnHeader: exportData.firstColumnHeader,
        secondColumnHeader: exportData.secondColumnHeader,
        total: exportData.total
      });

      this.analyticsExportService.exportDrinkVolumeTable(
        exportData,
        this.filters,
        'Drink Volume',
        this.drinkOptions,
        this.productOptions,
        this.distributorOptions,
        this.marketOptions,
        this.clientOptions,
        this.campusOptions,
        this.machineOptions
      );

      console.log('[Drink Volume Export] Export service call completed successfully');
    } catch (error) {
      console.error('[Drink Volume Export] Error exporting drink volume table:', error);
      throw error;
    }
  }
}

